/**
 * Order ticket — the user-facing trading form.
 *
 * SIM mode: 1-click submit (still runs Risk + Compliance via /orders/preview).
 * LIVE mode: opens a confirmation modal that requires:
 *   - the user's password
 *   - explicit "I understand" checkbox
 *   - 5-second cooldown timer before the Place button enables
 *
 * On success we push focal -> orders so the user sees the new fill.
 */
import { useEffect, useState } from 'react';
import * as api from '../lib/api';
import { useAuth } from '../lib/auth';
import { useFocal } from '../lib/focal';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../lib/toast';

interface Props {
  symbol: string;
}

export function OrderTicket({ symbol: initialSymbol }: Props) {
  const { me } = useAuth();
  const { set: setFocal } = useFocal();
  const navigate = useNavigate();
  const toast = useToast();
  const [symbol, setSymbol] = useState(initialSymbol.toUpperCase());
  const [side, setSide] = useState<'buy' | 'sell'>('buy');
  const [qty, setQty] = useState(1);
  const [orderType, setOrderType] = useState<'market' | 'limit'>('market');
  const [limitPrice, setLimitPrice] = useState<string>('');
  const [stopLoss, setStopLoss] = useState<string>('5');

  const [preview, setPreview] = useState<api.OrderPreview | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [placeBusy, setPlaceBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // LIVE confirm modal
  const [showLive, setShowLive] = useState(false);

  const isLive = me?.mode === 'live';

  // Auto-preview on form change (debounced lightly)
  useEffect(() => {
    if (!symbol) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setPreviewBusy(true);
      setError(null);
      try {
        const p = await api.orders.preview({
          symbol,
          side,
          quantity: qty,
          order_type: orderType,
          limit_price: orderType === 'limit' ? Number(limitPrice) || null : null,
          stop_loss_pct: stopLoss ? Number(stopLoss) : null,
        });
        if (!cancelled) setPreview(p);
      } catch (e: unknown) {
        if (!cancelled) {
          setPreview(null);
          setError(safeError(e));
        }
      } finally {
        if (!cancelled) setPreviewBusy(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [symbol, side, qty, orderType, limitPrice, stopLoss]);

  async function placeNow(liveAck: boolean) {
    if (!preview) return;
    setPlaceBusy(true);
    setError(null);
    try {
      const out = await api.orders.place(
        {
          symbol,
          side,
          quantity: qty,
          order_type: orderType,
          limit_price: orderType === 'limit' ? Number(limitPrice) || null : null,
          stop_loss_pct: stopLoss ? Number(stopLoss) : null,
        },
        liveAck,
      );
      // v0.5.6 AE-036 (LIVE fill truthfulness): only claim a fill when the
      // server says so. A LIVE order can come back "approved" = accepted by
      // Schwab but still working; the FillToasts SSE bridge announces the
      // real fill when it happens.
      if (out.status === 'filled') {
        toast.show(
          `Filled: ${side.toUpperCase()} ${qty} ${out.symbol} @ $${(out.fill_price ?? 0).toFixed(2)}`,
          'ok',
        );
      } else if (out.status === 'pending_approval') {
        toast.show(`Order pending approval: ${side.toUpperCase()} ${qty} ${out.symbol}`, 'info');
      } else {
        toast.show(`Order working at broker: ${side.toUpperCase()} ${qty} ${out.symbol}`, 'info');
      }
      setFocal(null);
      navigate('/approvals');
    } catch (e: unknown) {
      setError(safeError(e));
    } finally {
      setPlaceBusy(false);
      setShowLive(false);
    }
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!preview) return;
    if (!preview.risk_approved) {
      setError(`risk: ${preview.risk_violations.join(', ')}`);
      return;
    }
    if (!preview.compliance_approved) {
      setError(`compliance: ${preview.compliance_violations.join(', ')}`);
      return;
    }
    if (isLive) {
      setShowLive(true);
    } else {
      void placeNow(false);
    }
  }

  return (
    <div>
      <form onSubmit={onSubmit} className="grid grid-cols-2 gap-3 max-w-lg">
        <Field label="symbol">
          <input
            className="input w-full"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            maxLength={5}
            required
          />
        </Field>
        <Field label="side">
          <div className="flex gap-1">
            <button
              type="button"
              className={`btn flex-1 text-xs ${side === 'buy' ? 'border-ok text-ok' : ''}`}
              onClick={() => setSide('buy')}
            >
              BUY
            </button>
            <button
              type="button"
              className={`btn flex-1 text-xs ${side === 'sell' ? 'border-bad text-bad' : ''}`}
              onClick={() => setSide('sell')}
            >
              SELL
            </button>
          </div>
        </Field>
        <Field label="quantity">
          <input
            className="input w-full"
            type="number"
            min={0.0001}
            step="any"
            value={qty}
            onChange={(e) => setQty(Number(e.target.value))}
            required
          />
        </Field>
        <Field label="type">
          <select
            className="input w-full"
            value={orderType}
            onChange={(e) => setOrderType(e.target.value as 'market' | 'limit')}
          >
            <option value="market">market</option>
            <option value="limit">limit</option>
          </select>
        </Field>
        {orderType === 'limit' && (
          <Field label="limit price">
            <input
              className="input w-full num"
              type="number"
              step="any"
              value={limitPrice}
              onChange={(e) => setLimitPrice(e.target.value)}
              required
            />
          </Field>
        )}
        <Field label="stop loss %">
          <input
            className="input w-full num"
            type="number"
            step="0.5"
            min={0}
            max={100}
            value={stopLoss}
            onChange={(e) => setStopLoss(e.target.value)}
            placeholder="optional"
          />
        </Field>

        <div className="col-span-2">
          <PreviewBlock preview={preview} busy={previewBusy} />
        </div>

        {error && (
          <div
            className={`col-span-2 text-xs ${
              error.startsWith('✓') ? 'text-ok' : 'text-bad'
            }`}
          >
            {error}
          </div>
        )}

        <div className="col-span-2 flex justify-end gap-2">
          <button
            type="submit"
            className={isLive ? 'btn-danger' : 'btn-primary'}
            disabled={!preview || placeBusy || (preview && (!preview.risk_approved || !preview.compliance_approved))}
          >
            {isLive ? `Place LIVE ${side.toUpperCase()}` : `Place SIM ${side.toUpperCase()}`}
          </button>
        </div>
      </form>

      {showLive && (
        <LiveConfirmModal
          summary={
            preview
              ? `${side.toUpperCase()} ${qty} ${symbol} @ ${
                  orderType === 'limit' ? `limit $${limitPrice}` : `~$${preview.estimated_fill_price.toFixed(2)}`
                } · est cost $${preview.estimated_cost.toFixed(2)}`
              : ''
          }
          onCancel={() => setShowLive(false)}
          onConfirm={() => placeNow(true)}
          busy={placeBusy}
        />
      )}
    </div>
  );
}

function PreviewBlock({
  preview,
  busy,
}: {
  preview: api.OrderPreview | null;
  busy: boolean;
}) {
  if (busy && !preview) return <div className="text-xs text-ink-mute">previewing…</div>;
  if (!preview) return null;
  return (
    <div className="card p-3 text-xs num space-y-1">
      <div className="flex justify-between">
        <span className="text-ink-mute">est. fill</span>
        <span>${preview.estimated_fill_price.toFixed(2)}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-ink-mute">est. cost</span>
        <span>${preview.estimated_cost.toFixed(2)}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-ink-mute">mode</span>
        <span className={preview.mode === 'live' ? 'text-bad' : 'text-ok'}>
          {preview.mode.toUpperCase()}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-ink-mute">risk gate</span>
        <span className={preview.risk_approved ? 'text-ok' : 'text-bad'}>
          {preview.risk_approved ? 'ok' : preview.risk_violations.join(', ')}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-ink-mute">compliance</span>
        <span className={preview.compliance_approved ? 'text-ok' : 'text-bad'}>
          {preview.compliance_approved
            ? 'ok'
            : preview.compliance_violations.join(', ')}
        </span>
      </div>
    </div>
  );
}

function LiveConfirmModal({
  summary,
  onCancel,
  onConfirm,
  busy,
}: {
  summary: string;
  onCancel: () => void;
  onConfirm: () => void;
  busy: boolean;
}) {
  const [acked, setAcked] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(5);
  useEffect(() => {
    const id = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, []);
  const ready = acked && secondsLeft === 0;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="card p-5 max-w-md w-full">
        <div className="text-bad font-semibold mb-2 flex items-center gap-2">
          <span className="pill-live">LIVE</span> Confirm REAL MONEY order
        </div>
        <div className="text-sm num text-ink mb-4">{summary}</div>
        <label className="flex items-start gap-2 text-xs text-ink-mute mb-4">
          <input type="checkbox" checked={acked} onChange={(e) => setAcked(e.target.checked)} />
          <span>
            I understand this will execute against my real Schwab account and may result in real
            financial loss.
          </span>
        </label>
        <div className="flex justify-end gap-2">
          <button className="btn" onClick={onCancel} disabled={busy}>
            cancel
          </button>
          <button className="btn-danger" onClick={onConfirm} disabled={!ready || busy}>
            {busy
              ? 'placing…'
              : ready
              ? 'Place LIVE order'
              : `wait ${secondsLeft}s${acked ? '' : ' + ack'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[11px] text-ink-mute">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function safeError(e: unknown): string {
  const ae = e as { detail?: unknown; message?: string };
  if (ae?.detail && typeof ae.detail === 'object') {
    const d = ae.detail as Record<string, unknown>;
    if (d.detail && typeof d.detail === 'object') {
      const inner = d.detail as Record<string, unknown>;
      const reason = String(inner.reason ?? '');
      const violations = Array.isArray(inner.violations) ? (inner.violations as string[]) : [];
      return `${reason}${violations.length ? ': ' + violations.join(', ') : ''}`;
    }
  }
  return ae?.message ?? String(e);
}
